import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./TeamCalendar.module.css";

/* ── Day types ── */
type DayType = "game" | "practice" | "recovery" | "rest" | null;

const DAY_ICONS: Record<string, string> = {
  game: "\u{1F3C0}",
  practice: "\u{1F4AA}",
  recovery: "\u{1F504}",
  rest: "\u{1F634}",
};

/* March 2026 calendar data */
const MARCH_2026_START_DOW = 0; // March 1, 2026 = Sunday
const MARCH_2026_DAYS = 31;

/* Schedule for the month */
const SCHEDULE: Record<number, DayType> = {
  1: "rest",
  2: "practice",
  3: "practice",
  4: "game",
  5: "recovery",
  6: "practice",
  7: "game",
  8: "rest",
  9: "practice",
  10: "practice",
  11: "game",
  12: "recovery",
  13: "practice",
  14: "rest",
  15: "rest",
  16: "practice",
  17: "practice",
  18: "game",
  19: "recovery",
  20: "practice",
  21: "game",
  22: "rest",
  23: "practice",
  24: "practice",
  25: "game",
  26: "recovery",
  27: "practice",
  28: "rest",
  29: "rest",
  30: "practice",
  31: "practice",
};

/* Per-day team load level (simulated aggregate from all players) */
type TeamLoadLevel = "low" | "moderate" | "high" | "extreme";

const DAILY_TEAM_LOAD: Record<number, TeamLoadLevel> = {
  1: "low",
  2: "moderate",
  3: "moderate",
  4: "extreme",
  5: "low",
  6: "moderate",
  7: "extreme",
  8: "low",
  9: "moderate",
  10: "high",
  11: "extreme",
  12: "low",
  13: "moderate",
  14: "low",
  15: "low",
  16: "moderate",
  17: "high",
  18: "extreme",
  19: "low",
  20: "moderate",
  21: "extreme",
  22: "low",
  23: "moderate",
  24: "high",
  25: "extreme",
  26: "low",
  27: "moderate",
  28: "low",
  29: "low",
  30: "moderate",
  31: "moderate",
};

const LOAD_DOT_COLORS: Record<TeamLoadLevel, string> = {
  low: "#34c759",
  moderate: "#ffcc00",
  high: "#ff9500",
  extreme: "#ff3b30",
};

/* Weekly load segments (for the bar) */
const WEEK_LOAD = [
  { color: "#34c759", width: "25%" }, // low
  { color: "#ffcc00", width: "35%" }, // medium
  { color: "#ff9500", width: "25%" }, // high
  { color: "#ff3b30", width: "15%" }, // overload
];

/* Determine tough weeks: weeks with 2+ games or 3+ consecutive high/extreme days */
function getToughWeeks(): Set<number> {
  const toughWeeks = new Set<number>();

  // Week boundaries in March 2026 (Sun-Sat): Week1: 1-7, Week2: 8-14, Week3: 15-21, Week4: 22-28, Week5: 29-31
  const weeks = [
    [1, 7],
    [8, 14],
    [15, 21],
    [22, 28],
    [29, 31],
  ];

  for (let wi = 0; wi < weeks.length; wi++) {
    const [start, end] = weeks[wi];
    let gameCount = 0;
    let consecutiveHigh = 0;
    let hasConsecutiveHigh = false;

    for (let d = start; d <= end; d++) {
      if (SCHEDULE[d] === "game") gameCount++;
      const load = DAILY_TEAM_LOAD[d];
      if (load === "high" || load === "extreme") {
        consecutiveHigh++;
        if (consecutiveHigh >= 3) hasConsecutiveHigh = true;
      } else {
        consecutiveHigh = 0;
      }
    }

    if (gameCount >= 2 || hasConsecutiveHigh) {
      for (let d = start; d <= end; d++) {
        toughWeeks.add(d);
      }
    }
  }

  return toughWeeks;
}

export function TeamCalendar() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const legend: { icon: string; label: string }[] = [
    { icon: "\u{1F3C0}", label: t.aiTeam.calendarLegendGame },
    { icon: "\u{1F4AA}", label: t.aiTeam.calendarLegendPractice },
    { icon: "\u{1F504}", label: t.aiTeam.calendarLegendRecovery },
    { icon: "\u{1F634}", label: t.aiTeam.calendarLegendRest },
  ];

  const toughWeekDays = getToughWeeks();
  const opponents = t.aiTeam.calendarOpponents;

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const calendar = el.querySelector("[data-calendar]");
      if (calendar) {
        gsap.set(calendar, { y: 40, opacity: 0 });
        gsap.to(calendar, {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 75%",
            toggleActions: "play none none reverse",
          },
        });
      }

      const cells = el.querySelectorAll("[data-cal-cell]");
      if (isMobile) {
        gsap.set(cells, { opacity: 0, y: 20 });
        gsap.to(cells, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.01,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        });
      } else {
        gsap.set(cells, { scale: 0.8, opacity: 0 });
        gsap.to(cells, {
          scale: 1,
          opacity: 1,
          duration: 0.3,
          stagger: 0.015,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        });
      }

      /* Load bar fill animation */
      const loadSegments = el.querySelectorAll("[data-load-seg]");
      if (loadSegments.length > 0) {
        gsap.set(loadSegments, { scaleX: 0, transformOrigin: "left center" });
        gsap.to(loadSegments, {
          scaleX: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 60%",
            toggleActions: "play none none reverse",
          },
        });
      }

      /* Tough week badge pop */
      const toughBadge = el.querySelector("[data-tough-badge]");
      if (toughBadge) {
        gsap.set(toughBadge, { scale: 0, opacity: 0 });
        gsap.to(toughBadge, {
          scale: 1,
          opacity: 1,
          duration: 0.4,
          ease: "back.out(3)",
          scrollTrigger: {
            trigger: el,
            start: "top 65%",
            toggleActions: "play none none reverse",
          },
        });
      }
    },
    [isMobile],
  );

  /* Build calendar cells */
  const emptyCellKeys = ["e-sun", "e-mon", "e-tue", "e-wed", "e-thu", "e-fri", "e-sat"];
  const emptyCells = Array.from({ length: MARCH_2026_START_DOW }, (_, i) => (
    <div key={emptyCellKeys[i]} className={`${styles.dayCell} ${styles.dayEmpty}`} />
  ));

  const dayCells = Array.from({ length: MARCH_2026_DAYS }, (_, i) => {
    const day = i + 1;
    const type = SCHEDULE[day];
    const isToday = day === 17; // March 17
    const isTough = toughWeekDays.has(day);
    const teamLoad = DAILY_TEAM_LOAD[day];
    const opponent = opponents[day as keyof typeof opponents];

    return (
      <div
        key={day}
        className={`${styles.dayCell} ${isToday ? styles.dayToday : ""} ${isTough ? styles.dayTough : ""}`}
        data-cal-cell
      >
        <span className={styles.dayNumber}>{day}</span>
        {type && <span className={styles.dayIcon}>{DAY_ICONS[type]}</span>}
        {type === "game" && opponent && <span className={styles.dayOpponent}>{opponent}</span>}
        {teamLoad && (
          <span
            className={styles.dayLoadDot}
            style={{ background: LOAD_DOT_COLORS[teamLoad] }}
            title={t.aiTeam.calendarLoadSummary[teamLoad]}
          />
        )}
      </div>
    );
  });

  /* Check if current week (containing day 17) is tough */
  const currentWeekIsTough = toughWeekDays.has(17);

  return (
    <section ref={ref} className={styles.section}>
      <h2 className={styles.title}>{t.aiTeam.calendarTitle}</h2>
      <p className={styles.desc}>{t.aiTeam.calendarDesc}</p>

      <div className={styles.calendarWrap} data-calendar>
        <div className={styles.monthHeader}>
          <button type="button" className={styles.monthArrow}>
            {"\u25C0"}
          </button>
          <span className={styles.monthTitle}>{t.aiTeam.calendarMonthTitle}</span>
          <button type="button" className={styles.monthArrow}>
            {"\u25B6"}
          </button>
          {currentWeekIsTough && (
            <span className={styles.toughBadge} data-tough-badge>
              {t.aiTeam.calendarToughWeekLabel}
            </span>
          )}
        </div>

        <div className={styles.grid}>
          {t.aiTeam.calendarDayHeaders.map((d) => (
            <div key={d} className={styles.dayHeader}>
              {d}
            </div>
          ))}
          {emptyCells}
          {dayCells}
        </div>

        {/* Weekly load bar */}
        <div className={styles.weekLoad}>
          <span className={styles.weekLoadLabel}>{t.aiTeam.calendarWeekLoadLabel}</span>
          <div className={styles.weekLoadBar}>
            {WEEK_LOAD.map((seg) => (
              <div
                key={seg.color}
                className={styles.weekLoadSegment}
                style={{ width: seg.width, background: seg.color }}
                data-load-seg
              />
            ))}
          </div>
        </div>

        {/* Load level legend */}
        <div className={styles.loadLegend}>
          {(Object.entries(t.aiTeam.calendarLoadSummary) as [string, string][]).map(
            ([key, label]) => (
              <span key={key} className={styles.loadLegendItem}>
                <span
                  className={styles.loadLegendDot}
                  style={{
                    background: LOAD_DOT_COLORS[key as keyof typeof LOAD_DOT_COLORS],
                  }}
                />
                {label}
              </span>
            ),
          )}
        </div>

        {/* Legend */}
        <div className={styles.legend}>
          {legend.map((item) => (
            <span key={item.label} className={styles.legendItem}>
              {item.icon} {item.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
